import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/layout/AppShell";
import LoginPage from "@/pages/LoginPage";
import SetupPage from "@/pages/SetupPage";
import AgencyOverview from "@/pages/AgencyOverview";
import ImportsPage from "@/pages/ImportsPage";
import OpenTasksPage from "@/pages/OpenTasksPage";
import ClosedTasksPage from "@/pages/ClosedTasksPage";
import NewBusinessPage from "@/pages/NewBusinessPage";
import CancellationWatchPage from "@/pages/CancellationWatchPage";
import SettingsPage from "@/pages/SettingsPage";
import DueDateAuditPage from "@/pages/DueDateAuditPage";
import ScorecardsPage from "@/pages/ScorecardsPage";
import AlertsPage from "@/pages/AlertsPage";
import AlertRulesPage from "@/pages/AlertRulesPage";
import ReportBuilderPage from "@/pages/ReportBuilderPage";
import ReportArchivePage from "@/pages/ReportArchivePage";
import DeliveryMonitorPage from "@/pages/DeliveryMonitorPage";
import WorkforcePage from "@/pages/WorkforcePage";
import AskPage from "@/pages/AskPage";
import BotControlPage from "@/pages/BotControlPage";
import StagePlaceholder from "@/pages/StagePlaceholder";

function Protected() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-slate-500" data-testid="auth-loading">Checking session…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <AppShell><Outlet /></AppShell>;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  return user?.role === "admin" ? children : <StagePlaceholder title="Restricted" stage={null} description="This section is available to administrators only." />;
}

function StaffOnly({ children }) {
  const { user } = useAuth();
  return user?.role !== "employee" ? children : <StagePlaceholder title="Restricted" stage={null} description="This section is available to administrators and managers only." />;
}

function BotControlGate({ children }) {
  const { user } = useAuth();
  return user?.bot_control_role && user.bot_control_role !== "none" ? children : <StagePlaceholder title="Restricted" stage={null} description="The Bot Control Center requires a Bot Control role (viewer or higher). Ask a super admin to grant access." />;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/setup" element={<SetupPage />} />
            <Route element={<Protected />}>
              <Route path="/" element={<AgencyOverview />} />
              <Route path="/scorecards" element={<ScorecardsPage />} />
              <Route path="/tasks/open" element={<OpenTasksPage />} />
              <Route path="/tasks/closed" element={<ClosedTasksPage />} />
              <Route path="/due-date-audit" element={<DueDateAuditPage />} />
              <Route path="/new-business" element={<NewBusinessPage />} />
              <Route path="/cancellations" element={<CancellationWatchPage />} />
              <Route path="/alerts" element={<AlertsPage />} />
              <Route path="/alert-rules" element={<StaffOnly><AlertRulesPage /></StaffOnly>} />
              <Route path="/reports/builder" element={<AdminOnly><ReportBuilderPage /></AdminOnly>} />
              <Route path="/reports/archive" element={<AdminOnly><ReportArchivePage /></AdminOnly>} />
              <Route path="/imports" element={<AdminOnly><ImportsPage /></AdminOnly>} />
              <Route path="/delivery" element={<AdminOnly><DeliveryMonitorPage /></AdminOnly>} />
              <Route path="/workforce" element={<AdminOnly><WorkforcePage /></AdminOnly>} />
              <Route path="/ask" element={<AdminOnly><AskPage /></AdminOnly>} />
              <Route path="/bot-control/*" element={<BotControlGate><BotControlPage /></BotControlGate>} />
              <Route path="/settings/*" element={<AdminOnly><SettingsPage /></AdminOnly>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </div>
  );
}
